import React, { useRef } from 'react'
import { useParams } from 'react-router-dom';
import { Button, Container } from 'reactstrap';
import { useAuth } from '../../Services/AuthContext';
import { useFetchCached } from '../../Services/fetchApi';
import useFetch from '../../Services/useFetch';
import { endpoints } from '../../utils/Constants';
import LoadingAnimation from '../Form/LoadingAnimation';
import DocumentDetails from '../Editors/DocumentDetails';
import SupplierDocumentUploadGuidance from '../Form/SupplierDocumentUploadGuidance';
import { IDocumentRequirementItem } from '../../Models/IDocumentRequirementItem';
import { ISupplierDocumentUploadPolicy } from '../../Models/ISupplierDocumentUploadPolicy';
import '../Editors/DocumentDetails.css';

const compareRequirementNames = (left: IDocumentRequirementItem, right: IDocumentRequirementItem) =>
    (left.name || left.document?.fileCategory || '').localeCompare(right.name || right.document?.fileCategory || '', undefined, { sensitivity: 'base' });

const SupplierDocumentList = ({ route, supplierId: supp, charterRequestId }: { route?: string, supplierId?: string, charterRequestId?: string }) => {
    const params = useParams<Record<"supplierId", string>>();
    const [auth] = useAuth();
    const isAdmin = auth.role === 'Admin';
    const isSupplier = auth.role === 'Supplier';
    const canSeeGuidance = isAdmin || isSupplier;
    const supplierId = params.supplierId ?? auth.supplierId ?? supp;
    const requirementsUrl = supplierId ? endpoints.getSupplierDocumentRequirements + supplierId + (charterRequestId ? '?charterRequestId=' + encodeURIComponent(charterRequestId) : '') : null;
    const [requirements, loading, error, refresh] = useFetch<IDocumentRequirementItem[]>(requirementsUrl);
    const [uploadPolicy] = useFetchCached<ISupplierDocumentUploadPolicy>(endpoints.getSupplierDocumentUploadPolicy);
    // Keep showing the last loaded documents if a background refresh (e.g. after upload) fails.
    const lastRequirementsRef = useRef<IDocumentRequirementItem[] | undefined>(undefined);
    if (requirements) lastRequirementsRef.current = requirements;
    const displayRequirements = requirements ?? lastRequirementsRef.current;
    const initialLoad = loading && !displayRequirements;
    // Insurance document types belong to the Insurance Documents tab only.
    const operatorRequirements = displayRequirements?.filter(r => !r.isInsurance);
    const docs = operatorRequirements?.filter(r => r.isRequired).slice().sort(compareRequirementNames);
    const optionalDocs = operatorRequirements?.filter(r => !r.isRequired).slice().sort(compareRequirementNames);

    return <Container className="doc-list">
        {canSeeGuidance ? <SupplierDocumentUploadGuidance policy={uploadPolicy} isAdmin={isAdmin} isSupplier={isSupplier} /> : null}
        {initialLoad ? <LoadingAnimation /> : error ? <h3>Error<Button onClick={() => refresh()}>Retry</Button></h3> : null}
        {docs && docs.length > 0 && <>
            <h3 className="doc-list__section-title">Required Documents</h3>
            {docs.map((requirement, i) => <DocumentDetails key={requirement.code || i} doc={requirement.document} refresh={refresh} supplierId={supplierId} uploadPolicy={uploadPolicy} />)}
        </>}
        {optionalDocs && optionalDocs.length > 0 && <>
            <h3 className="doc-list__section-title">Optional Documents</h3>
            {optionalDocs.map((requirement, i) => <DocumentDetails optional key={requirement.code || i} doc={requirement.document} refresh={refresh} supplierId={supplierId} uploadPolicy={uploadPolicy} />)}
        </>}
    </Container>

}

export default SupplierDocumentList;


